import type { FC, ImgHTMLAttributes } from "react";
import React, { useState } from "react";

import cn from "classnames";

import { PLACEHOLDER_ASSETS } from "./utils/placeholderAssets";
import { IconSizeErrorBoundary } from "../../../../HOCs/IconSizeErrorBoundary";
import { Icon } from "../../../Icon";
import { UniBoxAdditional } from "../../../UniBox";
import { ICON_SIZE_BY_AVA_PICTURE_SIZE_MAP } from "../../constants/sizes";
import { createDisplayNameForAvaPictureElement } from "../../utils/getDisplayName";
import { createTestIdForAvaPictureElement } from "../../utils/getTestId";

import type {
    OmitOfUnion,
    PropsWithCustomVarsStyles,
} from "../../../../types/react";
import type { IconProps } from "../../../Icon";
import type { AvaPictureShapes, AvaPictureSizes } from "../../types";

import classes from "./AvaPictureImage.styl";

export type AvaPicturePlaceholderTypes = "profile" | "group";

interface AvaPictureImageCoreProps
    extends Omit<
        ImgHTMLAttributes<HTMLImageElement>,
        "className" | "style" | "src" | "alt"
    > {
    /** Атрибут alt изображения */
    alt: string;
    /** URL изображения. Если нет, то будет использоваться placeholder */
    src?: string;
    /** Отвечает за отображение внутренней обводки изображения */
    imageBorder?: boolean;
    /** Отвечает за отображение оверлея с иконкой поверх изображения */
    overlay?: boolean;
    /** Отвечает за выбор инстанса иконки из библиотеки */
    overlayIcon?: OmitOfUnion<IconProps, "size" | "className">["icon"];
    /** Отвечает за тип плейсхолдера, отображаемого до загрузки основного изображения */
    placeholderType?: AvaPicturePlaceholderTypes;
    /** Кастомный URL для плейсхолдера. Если не указан, используется стандартный плейсхолдер на основе placeholderType */
    placeholderSrc?: string;
}

type AvaPictureImagePrivateProps = PropsWithCustomVarsStyles<
    AvaPictureImageCoreProps & {
        smoothRadius: React.CSSProperties;
        shape: AvaPictureShapes;
        size: AvaPictureSizes;
    }
>;

export type AvaPictureImageProps = AvaPictureImageCoreProps;

const AVA_PICTURE_IMAGE_DISPLAY_NAME =
    createDisplayNameForAvaPictureElement("Image");
export const AVA_PICTURE_IMAGE_TEST_ID =
    createTestIdForAvaPictureElement("image");
export const AVA_PICTURE_IMAGE_OVERLAY_TEST_ID =
    createTestIdForAvaPictureElement("image-overlay");

export const AvaPictureImage: FC<AvaPictureImageProps> = (props) => {
    const {
        src,
        alt,
        shape,
        size,
        imageBorder = true,
        overlay = false,
        overlayIcon,
        placeholderType = "profile",
        placeholderSrc,
        customVarsStyles,
        ...restProps
    } = props as AvaPictureImagePrivateProps;

    const [imageError, setImageError] = useState(false);

    const iconSize = ICON_SIZE_BY_AVA_PICTURE_SIZE_MAP[size];

    const iconProps = {
        size: iconSize,
        icon: overlayIcon,
        className: classes["overlay-icon"],
    } as IconProps;

    const placeholderStyles = () => {
        if (placeholderSrc) {
            return {
                "--ava-picture-image-custom-placeholder": `url("${placeholderSrc}")`,
            };
        }

        const assets = PLACEHOLDER_ASSETS[placeholderType];
        return {
            [`--ava-picture-image-placeholder-${placeholderType}-light`]: `url('${assets.light}')`,
            [`--ava-picture-image-placeholder-${placeholderType}-dark`]: `url('${assets.dark}')`,
        };
    };

    const classNames = cn(
        classes["image-wrapper"],
        imageBorder && classes["__inner-border"],
        classes[`__shape-${shape}`],
        classes[`__size-${size}`],
        placeholderSrc
            ? classes["__placeholder-custom"]
            : classes[`__placeholder-${placeholderType}`],
        customVarsStyles,
    );
    return (
        <div
            data-tsid={AVA_PICTURE_IMAGE_TEST_ID}
            className={classNames}
            style={placeholderStyles()}
        >
            {!imageError && src && (
                <img
                    {...restProps}
                    src={src}
                    alt={alt}
                    className={classes["image"]}
                    onError={() => setImageError(true)}
                />
            )}
            {overlay && (
                <UniBoxAdditional
                    data-tsid={AVA_PICTURE_IMAGE_OVERLAY_TEST_ID}
                    customVarsStyles={cn(
                        classes["overlay-uni-box-custom-vars"],
                        classes[`__shape-${shape}`],
                    )}
                >
                    <UniBoxAdditional.SlotMiddle
                        customVarsStyles={
                            classes["overlay-uni-box-slot-custom-vars"]
                        }
                    >
                        {overlayIcon && (
                            <IconSizeErrorBoundary
                                componentName={AVA_PICTURE_IMAGE_DISPLAY_NAME}
                                componentSize={size}
                                iconName={overlayIcon}
                                iconSize={iconSize}
                            >
                                <Icon {...iconProps} />
                            </IconSizeErrorBoundary>
                        )}
                    </UniBoxAdditional.SlotMiddle>
                </UniBoxAdditional>
            )}
        </div>
    );
};

AvaPictureImage.displayName = AVA_PICTURE_IMAGE_DISPLAY_NAME;

export default AvaPictureImage;
